import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

export interface ContributeSegmentPayload {
  model_id: number;
  segment_ids: number[];
  anchor_segment_id: number;
}

export interface ContributeReadiness {
  clips_count: number;
  clips_required: number;
  status: 'processing' | 'queued' | 'failed';
  /** Progress in audio, the rule training checks. */
  total_duration_seconds?: number;
  required_duration_seconds?: number;
  /** What this request did. Optional so an older API still parses. */
  clips_added?: number;
  clips_already_contributed?: number;
  /** Clips already on the model whose text was rewritten from the document. */
  clips_updated?: number;
  seconds_added?: number;
  /** Whole-file contributions only. */
  file_added?: boolean;
  file_updated?: boolean;
  seconds_transcribed?: number;
}

export const useContributeSegment = (sessionId: string, documentId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    ContributeReadiness,
    AxiosError<BaseError>,
    ContributeSegmentPayload
  >(
    async payload => {
      const { data } = await Axios.post<ContributeReadiness>(
        POST_URL(sessionId, documentId).contributeTrainingClip,
        payload
      );
      return data;
    },
    {
      // Without invalidation the model detail page keeps showing "No
      // contributions yet" because the datasets query is cached and the
      // conditional polling only kicks in once a PROCESSING row is already
      // visible. Invalidate the model's data + detail so the freshly
      // contributed clip appears immediately when the user navigates over.
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(
          CUSTOM_MODEL_QUERY_KEYS.data(variables.model_id)
        );
        queryClient.invalidateQueries(
          CUSTOM_MODEL_QUERY_KEYS.detail(variables.model_id)
        );
      }
    }
  );
};
